import { useCallback, useMemo } from 'react';
import { unprojectToPlane, SCENE_VIEW } from '../engine/battlefield/projection';
import { clampToBoundary, boundaryContains } from '../engine/battlefield/boundary';
import { defaultRing, deploy, lineSlots, areaSlots } from '../engine/battlefield/deployment';
import { solidBounds } from '../engine/battlefield/solids';
import { pointerToScene } from '../utils/scenePointer';
import { solidUnderPointer } from '../utils/solidUnder';

/** A stable id, never Math.random() - the dice own that (utils/entropy.ts). */
let markerCounter = 0;
const nextMarkerId = () => `deploy-${(markerCounter += 1).toString(36)}`;

/** What a deployment of nobody returns - a fresh object each time, as callers may keep it. */
const nobody = () => ({ placed: {}, clamped: [], unavoided: [] });

/**
 * THE SURFACE UNDER THE POINTER, not the floor beneath it: the top of the
 * solid the click landed on, or 0. Planting on a gantry is how a GM says
 * which storey they are deploying to; the same top-plane hit test the
 * pings use (v7.82.0), a line of sight on the GPU map (utils/solidUnder).
 * Hidden terrain counts for the GM alone: a player granted deployment never
 * plants on what they cannot see (that would tell them it is there).
 */
function surfaceLevel(e, solids, camera, isGm) {
  const pressed = solids ? solidUnderPointer(e, { solids, camera, includeHidden: !!isGm }) : null;
  return pressed ? solidBounds(pressed).max.y : 0;
}

/** The marker at a point, keeping its id and ring when it already exists. */
function plantedMarker(marker, p, level) {
  return {
    id: marker?.id ?? nextMarkerId(),
    x: p.x,
    z: p.z,
    ...(level ? { y: level } : {}),
    ring: marker?.ring ?? defaultRing(),
  };
}

/** The marker with a team to stamp, or none: `null` keeps its place and ring and stops stamping. */
function withTeam(marker, team) {
  const { team: _dropped, ...rest } = marker;
  return team ? { ...rest, team } : rest;
}

/**
 * The marker raised or lowered, in metres above whatever it stands on.
 * `next` may be a function of (current air, one tile). ONE TILE PER NOTCH,
 * the same as raising somebody in Play, and never below the surface it was
 * planted on - down is the ground, not a burrow. Zero is the ordinary
 * state, which is what the reset button returns to.
 */
function withAir(marker, next, tileSize) {
  const step = tileSize > 0 ? tileSize : 1;
  const air = typeof next === 'function' ? next(marker.air ?? 0, step) : next;
  return { ...marker, air: Math.max(0, Math.round(air * 100) / 100) };
}

/**
 * useDeployment - planting the marker, and ringing people around it.
 *
 * The panel keeps the gesture; this keeps the decisions. Everything
 * geometric lives in `engine/battlefield/deployment.ts` and is tested
 * there - what is left here is turning a click into a ground point and
 * handing the result to the sync layer.
 *
 * DEPLOYMENT HAPPENS ON ARRIVAL, at wherever the marker stands at that
 * moment. Moving the marker afterwards moves nobody, which is what makes
 * the workflow work: plant it, import the players, move it, import the
 * opposition.
 */
export default function useDeployment(params) {
  const { marker, setMarker, getCamera, boundary, tokens } = params;
  const groundPoint = useGroundPoint(getCamera);
  const { plantAt, removeArrival } = usePlanting(params, groundPoint);
  const { setRing, setAir, setTeam } = useMarkerEdits(marker, setMarker, boundary);
  const { deployThese, deployAlong, deployArea } = useFormations(params);
  /** Everyone currently on the map, for a re-deploy. */
  const deployedTokens = useMemo(() => Object.values(tokens || {}), [tokens]);
  // `groundPoint` is exposed because the hover readout needs the same
  // screen-to-ground conversion and there should be one of them.
  return {
    ...{ plantAt, setRing, setAir, setTeam, removeArrival },
    ...{ deployThese, deployAlong, deployArea, deployedTokens, groundPoint },
  };
}

/**
 * Plant it, or move it. Never outside the arena. With a team chosen (`plantFor`, plan scene-editor-library, B) the
 * click plants THAT team's arrival point instead, and the main marker stays where it is; `removeArrival` takes a
 * team's point away, so its people arrive at the main marker again.
 */
function usePlanting(
  { marker, setMarker, boundary, solids, getCamera, isGm, plantFor, arrivals, setArrivals },
  groundPoint,
) {
  const plantAt = useCallback(
    (e) => {
      const level = surfaceLevel(e, solids, getCamera(), isGm);
      const at = groundPoint(e, level);
      if (!at) return null;
      const p = boundaryContains(boundary, at.x, at.z) ? at : clampToBoundary(boundary, at.x, at.z);
      if (plantFor && setArrivals)
        setArrivals({ ...arrivals, [plantFor]: plantedMarker(arrivals?.[plantFor], p, level) });
      else setMarker(plantedMarker(marker, p, level));
      // WHERE IT WENT, returned - a formation drag starts at the marker
      // and cannot wait a render for the state to come back (v8.22.0).
      return { x: p.x, y: level, z: p.z };
    },
    [groundPoint, boundary, marker, setMarker, solids, getCamera, isGm, plantFor, arrivals, setArrivals],
  );
  const removeArrival = useCallback(
    (tag) => {
      const { [tag]: _gone, ...rest } = arrivals ?? {};
      setArrivals?.(rest);
    },
    [arrivals, setArrivals],
  );
  return { plantAt, removeArrival };
}

/**
 * The ground under the pointer - or the top of whatever it is over.
 *
 * `planeY` is the height to unproject ONTO, and it matters more than it
 * looks. At y=0 the answer for a pointer over a three-metre wall is the
 * ground BEHIND the wall, because that is where the ray through the cursor
 * meets the floor. Passing the wall's own top height instead gives the
 * point genuinely under the cursor, at the height it appears to be - which
 * is what a ping aimed at a rooftop means (v7.82.0).
 */
function useGroundPoint(getCamera) {
  return useCallback(
    (e, planeY = 0) => {
      const { x, y } = pointerToScene(e);
      return unprojectToPlane(x, y, planeY, getCamera(), SCENE_VIEW);
    },
    [getCamera],
  );
}

/** The marker's ring, team and height. Each does nothing without a marker. */
function useMarkerEdits(marker, setMarker, boundary) {
  const setRing = useCallback(
    (patch) => {
      if (marker) setMarker({ ...marker, ring: { ...marker.ring, ...patch } });
    },
    [marker, setMarker],
  );
  const setTeam = useCallback(
    (team) => {
      if (marker) setMarker(withTeam(marker, team));
    },
    [marker, setMarker],
  );
  const setAir = useCallback(
    (next) => {
      if (marker) setMarker(withAir(marker, next, boundary.tileSize));
    },
    [marker, setMarker, boundary],
  );
  return { setRing, setAir, setTeam };
}

/**
 * Ringing, lining up and filling an area.
 *
 * `deployThese` rings these combatants at the marker and writes them - used
 * both by an arrival and by the deliberate re-deploy button, so there is
 * one path and one set of rules for both. The line and the area strike the
 * same bargain: the shape is a parameter and everything else - clamping,
 * settling, stepping aside - is the ring's. THE MARKER IS NOT MOVED by a
 * line: it has its own two ends and the marker keeps meaning what it
 * meant; a drag that silently re-planted it would lose whichever storey
 * the GM had chosen.
 */
function useFormations({ marker, boundary, solids, avoid, groundAt, writeToken }) {
  // `at` rings them somewhere other than the marker: a team's own arrival point (plan scene-editor-library, B).
  const deployThese = useCallback(
    (list, slots, at = marker) => {
      if (!at || !list?.length) return nobody();
      const out = deploy(list, at, boundary, solids, avoid, groundAt, slots);
      for (const token of Object.values(out.placed)) writeToken(token, { immediate: true });
      return out;
    },
    [marker, boundary, solids, avoid, groundAt, writeToken],
  );
  /** A formation: nobody without a marker or a list, else `slots(list)` handed to deployThese. */
  const formation = useCallback(
    (list, slots) => (!marker || !list?.length ? nobody() : deployThese(list, slots(list))),
    [marker, deployThese],
  );
  const deployArea = useCallback(
    (list, from, to, step) => formation(list, (l) => areaSlots(from, to, l.length, step)),
    [formation],
  );
  const deployAlong = useCallback(
    (list, from, to, step) => formation(list, (l) => lineSlots(from, to, l.length, step)),
    [formation],
  );
  return { deployThese, deployAlong, deployArea };
}
